import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Gauge, RefreshCw, Save, Loader2, BellRing } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const BAL_LABEL = { avadata: "AvaData", postgrid: "PostGrid", attom: "ATTOM", rentcast: "Rentcast", retell: "Retell AI", realvalidation: "RealValidation (DNC)", universal_key: "Universal Key (AI)" };
const COST_LABEL = {
  attom: "ATTOM / call", rentcast: "Rentcast / call", avadata_standard: "AvaData skip-trace / call",
  avadata_deep: "AvaData Deep Search / call", ai: "GPT-5.4 / lead",
  email: "Email / recipient", postgrid: "PostGrid / postcard", stripe_pct: "Stripe fee %", stripe_fixed: "Stripe fixed fee",
};

function BalanceCard({ id, b }) {
  return (
    <Card className={`p-4 ${b.low ? "border-red-300 bg-red-50/40" : ""}`} data-testid={`balance-${id}`}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{BAL_LABEL[id]}</div>
      {b.available
        ? <div className="mt-1 font-display text-2xl font-extrabold text-slate-900" data-testid={`balance-${id}-value`}>
            {b.unit === "usd" ? usd(b.value) : Number(b.value).toLocaleString()} {b.unit !== "usd" && <span className="text-sm font-semibold text-slate-500">{b.unit}</span>}
          </div>
        : <div className={`mt-1 text-sm font-semibold ${b.low ? "text-red-700" : "text-slate-600"}`} data-testid={`balance-${id}-note`}>{b.note}</div>}
      {b.detail && <div className="mt-1 text-xs text-slate-500">{b.detail}</div>}
      {b.billing_url && <a href={b.billing_url} target="_blank" rel="noreferrer" data-testid={`add-credits-${id}`}
        className="mt-3 inline-flex items-center rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-slate-700">{["retell", "realvalidation", "universal_key"].includes(id) ? "Refill credits ↗" : "Add credits ↗"}</a>}
    </Card>
  );
}

function PeriodTable({ period, testId }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead className="text-left text-xs uppercase tracking-wider text-slate-400">
          <tr><th className="py-2 pr-4">Service</th><th className="py-2 pr-4 text-right">Calls</th><th className="py-2 text-right">Est. spend</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {period.rows.map((r) => (
            <tr key={r.provider} data-testid={`${testId}-${r.provider}`}>
              <td className="py-2 pr-4 text-slate-700">{r.label}</td>
              <td className="py-2 pr-4 text-right tabular-nums text-slate-600">{r.calls.toLocaleString()}</td>
              <td className="py-2 text-right font-semibold tabular-nums text-slate-900">{usd(r.spend)}</td>
            </tr>
          ))}
          <tr><td className="py-2 font-bold text-slate-900">Total</td><td /><td className="py-2 text-right font-extrabold text-slate-900" data-testid={`${testId}-total`}>{usd(period.total)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function CostEditor({ costs, onSaved }) {
  const [c, setC] = useState(costs);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { onSaved((await api.put("/admin/api-usage/costs", Object.fromEntries(Object.entries(c).map(([k, v]) => [k, parseFloat(v) || 0])))).data); toast.success("Cost estimates saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Card className="p-5">
      <h3 className="font-display font-bold text-slate-900">Cost per call (estimates)</h3>
      <p className="mb-4 text-xs text-slate-500">Starter estimates — replace with your actual plan rates. Spend is recalculated from these.</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Object.keys(COST_LABEL).map((k) => (
          <label key={k} className="text-xs font-semibold text-slate-600">{COST_LABEL[k]}
            <Input type="number" step="0.001" min="0" className="mt-1" value={c[k] ?? ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} data-testid={`cost-${k}`} />
          </label>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="mt-4 bg-slate-900 hover:bg-slate-800" data-testid="save-costs-btn">
        {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save estimates
      </Button>
    </Card>
  );
}

function AlertCard({ alert, credits, onSaved }) {
  const [a, setA] = useState(alert);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/api-usage/alert", { enabled: a.enabled, threshold: parseInt(a.threshold, 10) || 0 }); toast.success("Low-credit alert saved"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const low = credits !== null && credits < Number(alert.threshold);
  return (
    <Card className="p-5" data-testid="alert-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><BellRing className="h-5 w-5 text-amber-600" /><h3 className="font-display font-bold text-slate-900">AvaData low-credit alert</h3></div>
        <label className="flex items-center gap-2 text-sm text-slate-600">Email admins<Switch checked={!!a.enabled} onCheckedChange={(v) => setA({ ...a, enabled: v })} data-testid="alert-enabled-switch" /></label>
      </div>
      <p className="mt-1 text-xs text-slate-500">Checked after every lead run and daily at 8 AM ET. Sends one email per drop below the limit; re-arms after you top up.</p>
      {low && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="alert-low-banner">Credits ({credits}) are below your alert limit — top up AvaData to keep contact lookups running.</div>}
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-slate-600">Alert when credits fall below
          <Input type="number" min="0" className="mt-1 w-32" value={a.threshold} onChange={(e) => setA({ ...a, threshold: e.target.value })} data-testid="alert-threshold-input" />
        </label>
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="save-alert-btn">{saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save alert</Button>
        {alert.last_sent_at && <span className="text-xs text-slate-500" data-testid="alert-last-sent">Last alert: {new Date(alert.last_sent_at).toLocaleString()}</span>}
      </div>
    </Card>
  );
}

export default function AdminApiUsage() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { setD((await api.get("/admin/api-usage")).data); } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="API Credits & Spend" subtitle="Live balances where providers expose them, plus estimated spend per 2-week period (every other Tuesday from Oct 6).">
        <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-usage-btn"><RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
      </PageHeader>
      {!d ? <div className="p-10 text-center text-sm text-slate-400"><Gauge className="mx-auto mb-2 h-8 w-8 text-slate-300" />Loading usage…</div> : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Object.entries(d.balances).map(([k, b]) => <BalanceCard key={k} id={k} b={b} />)}
          </div>
          <AlertCard key={JSON.stringify(d.alert)} alert={d.alert} credits={d.balances.avadata.available ? Number(d.balances.avadata.value) : null} onSaved={load} />
          <Card className="p-5">
            <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
              <div>
                <h3 className="font-display font-bold text-slate-900">Current period</h3>
                <p className="text-xs text-slate-500" data-testid="current-period-range">{d.current.start} → {d.current.end} · usage counted since {d.tracking_since}</p>
              </div>
              <div className="font-display text-3xl font-extrabold text-slate-900" data-testid="current-period-total">{usd(d.current.total)}</div>
            </div>
            <PeriodTable period={d.current} testId="current-period-table" />
          </Card>
          <Card className="p-5">
            <h3 className="mb-3 font-display font-bold text-slate-900">Past periods</h3>
            {d.history.length === 0
              ? <p className="text-sm text-slate-400" data-testid="history-empty">No completed 2-week periods yet — the first closes {d.current.end}.</p>
              : <div className="space-y-5">{d.history.map((p, i) => (
                  <div key={p.start}><div className="mb-1 flex justify-between text-sm font-semibold text-slate-700"><span>{p.start} → {p.end}</span><span>{usd(p.total)}</span></div>
                  <PeriodTable period={p} testId={`history-${i}`} /></div>))}</div>}
          </Card>
          <CostEditor key={JSON.stringify(d.costs)} costs={d.costs} onSaved={() => load()} />
        </div>
      )}
    </div>
  );
}
